/**
 * /safety/training/courses — Course Management: the course cards, full width,
 * and the Question Builder tab. A card opens the course's form on its own
 * page (/safety/training/courses/:courseId/edit) and New Course opens
 * /safety/training/courses/new (CourseEditScreen); the form used to be a
 * slide-over drawer here, until split panes and drawers beside a list were
 * banned (CLAUDE.md). The tab is in the URL (`?tab=questions`) so a back arrow
 * returns to it.
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Video, GripVertical, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { useOpenRecord } from "../downhole/recordPage";
import { useTrainingCourses, validityLabel, type Course } from "./trainingCourses";


/* ────────────── types ────────────── */

interface Question {
  id?: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: "A" | "B" | "C" | "D";
  sort_order: number;
}

/* ────────────── main component ────────────── */

const CourseManagement = () => {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: "courses" | "questions" = searchParams.get("tab") === "questions" ? "questions" : "courses";
  const setTab = (next: "courses" | "questions") => {
    const p = new URLSearchParams(searchParams);
    if (next === "questions") p.set("tab", next);
    else p.delete("tab");
    setSearchParams(p, { replace: true });
  };

  // Courses list
  const coursesQuery = useTrainingCourses();
  const { isLoading } = coursesQuery;
  const courses = coursesQuery.data ?? [];

  const tabs = [
    { key: "courses" as const, label: "Courses" },
    { key: "questions" as const, label: "Question Builder" },
  ];

  return (
    <div className="min-h-screen pb-[var(--bottom-chrome)] bg-background">
      {/* Action bar */}
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/safety/training")} className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-foreground">Course Management</h1>
          </div>
          {tab === "courses" && (
            <button
              onClick={() => openRecord("/safety/training/courses/new")}
              className="h-9 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Course
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border px-6 flex items-center gap-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-6 py-4 pb-20">
        {tab === "courses" ? (
          <CoursesList
            courses={courses}
            isLoading={isLoading}
            failure={queryLoadFailure(coursesQuery) ? queryFailureError(coursesQuery) : null}
            onEdit={(c) => openRecord(`/safety/training/courses/${c.id}/edit`)}
          />
        ) : (
          <QuestionBuilder courses={courses} />
        )}
      </div>
    </div>
  );
};

/* ────────────── Courses List ────────────── */

const CoursesList = ({
  courses,
  isLoading,
  failure,
  onEdit,
}: {
  courses: Course[];
  isLoading: boolean;
  failure: unknown;
  onEdit: (c: Course) => void;
}) => {
  if (isLoading) {
    return <div className="text-center py-12 text-muted-foreground text-sm">Loading courses...</div>;
  }

  // A list that did not load is not "no courses yet".
  if (failure) {
    return <LoadFailureNotice error={failure} subject="training courses" />;
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm">
        No courses yet. Click "New Course" to create one.
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {courses.map((course) => (
        <div
          key={course.id}
          role="button"
          tabIndex={0}
          className="bg-fieldiq-card border border-border rounded-xl p-5 space-y-3 cursor-pointer hover:border-primary/40 transition-colors outline-none focus-visible:bg-fi-amber/10"
          onClick={() => onEdit(course)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onEdit(course);
            }
          }}
        >
          <div className="flex items-start justify-between">
            <h3 className="text-sm font-semibold text-foreground leading-tight">{course.title}</h3>
            {course.video_url && <Video className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />}
          </div>
          {course.description && (
            <p className="text-xs text-muted-foreground line-clamp-2">{course.description}</p>
          )}
          <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
            <span className="px-2 py-0.5 rounded-full bg-secondary">{course.question_count ?? 0} questions</span>
            <span className="px-2 py-0.5 rounded-full bg-secondary">{validityLabel(course.validity_months)}</span>
            <span className="px-2 py-0.5 rounded-full bg-secondary">{course.passing_score}% to pass</span>
          </div>
          <div className="flex items-center justify-end pt-1">
            <span className="text-[10px] text-muted-foreground">{course.question_pool_size ?? "—"} per quiz</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ────────────── Question Builder ────────────── */

const emptyQuestion = (order: number): Question => ({
  question_text: "",
  option_a: "",
  option_b: "",
  option_c: "",
  option_d: "",
  correct_answer: "A",
  sort_order: order,
});

const QuestionBuilder = ({ courses }: { courses: Course[] }) => {
  const queryClient = useQueryClient();
  const [selectedCourseId, setSelectedCourseId] = useState(courses[0]?.id ?? "");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const selectedCourse = courses.find((c) => c.id === selectedCourseId);

  const { data: loadedQuestions, isLoading } = useQuery<Question[]>({
    queryKey: ["training-questions", selectedCourseId],
    queryFn: () =>
      api.safety.trainingCourseQuestions(selectedCourseId).then((r: any) => r.data?.data ?? r.data ?? []),
    enabled: !!selectedCourseId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (loadedQuestions) setQuestions(loadedQuestions);
  }, [loadedQuestions]);

  const addQuestion = () => {
    const next = [...questions, emptyQuestion(questions.length + 1)];
    setQuestions(next);
    setExpandedIdx(next.length - 1);
  };

  const updateQuestion = (idx: number, patch: Partial<Question>) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const removeQuestion = (idx: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx).map((q, i) => ({ ...q, sort_order: i + 1 })));
    setExpandedIdx(null);
  };

  const handleSaveAll = async () => {
    if (!selectedCourseId) return;
    const valid = questions.filter((q) => q.question_text.trim());
    if (valid.length === 0) { toast.error("Add at least one question"); return; }
    setSaving(true);
    try {
      await api.safety.saveTrainingCourseQuestions(selectedCourseId, { questions: valid });
      toast.success(`Saved ${valid.length} questions`);
      queryClient.invalidateQueries({ queryKey: ["training-questions", selectedCourseId] });
      queryClient.invalidateQueries({ queryKey: ["training-courses"] });
    } catch {
      toast.error("Failed to save questions");
    } finally {
      setSaving(false);
    }
  };

  if (!courses.length) {
    return <div className="text-center py-12 text-muted-foreground text-sm">Create a course first.</div>;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Course selector */}
      <div className="flex items-center gap-4 flex-wrap pb-4 shrink-0">
        <select
          value={selectedCourseId}
          onChange={(e) => { setSelectedCourseId(e.target.value); setExpandedIdx(null); }}
          className="h-9 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50 min-w-[200px]"
        >
          {courses.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
        {selectedCourse && (
          <span className="text-xs text-muted-foreground">
            {questions.length} questions / {selectedCourse.question_pool_size ?? "—"} per quiz
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Loading questions...</div>
      ) : (
        <>
          {/* Question list */}
          <div className="overflow-y-auto flex-1 space-y-2">
            {questions.map((q, idx) => (
              <div key={idx} className="border border-border rounded-lg overflow-hidden">
                {/* Collapsed row */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-xs text-muted-foreground font-mono w-6">{idx + 1}.</span>
                  <span className="text-sm text-foreground flex-1 truncate">
                    {q.question_text || "(new question)"}
                  </span>
                  {q.question_text && q.option_a && (
                    <span className="text-[10px] text-muted-foreground">
                      {[q.option_a, q.option_b, q.option_c, q.option_d].filter(Boolean).length} options · Answer: {q.correct_answer}
                    </span>
                  )}
                </div>

                {/* Expanded form */}
                {expandedIdx === idx && (
                  <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">Question</label>
                      <textarea
                        value={q.question_text}
                        onChange={(e) => updateQuestion(idx, { question_text: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50 resize-none"
                        placeholder="Enter question text..."
                      />
                    </div>
                    {(["A", "B", "C", "D"] as const).map((letter) => {
                      const key = `option_${letter.toLowerCase()}` as keyof Question;
                      return (
                        <div key={letter} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`correct-${idx}`}
                            checked={q.correct_answer === letter}
                            onChange={() => updateQuestion(idx, { correct_answer: letter })}
                            className="accent-primary"
                          />
                          <span className="text-xs font-medium text-muted-foreground w-4">{letter}</span>
                          <input
                            value={(q[key] as string) || ""}
                            onChange={(e) => updateQuestion(idx, { [key]: e.target.value })}
                            className="flex-1 h-8 px-3 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-primary/50"
                            placeholder={`Option ${letter}`}
                          />
                        </div>
                      );
                    })}
                    <div className="flex justify-end">
                      <button
                        onClick={() => removeQuestion(idx)}
                        className="text-xs text-red-400 hover:text-red-300 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Actions — pinned footer, stays visible above the mobile nav */}
          <div className="border-t border-border p-4 bg-background shrink-0 flex gap-3">
            <button
              onClick={addQuestion}
              className="h-9 px-4 border border-border text-foreground text-sm font-medium rounded-lg hover:bg-secondary/50 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add Question
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || questions.length === 0}
              className="h-9 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              {saving ? "Saving..." : "Save All Questions"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CourseManagement;
